import { FlowButton } from 'components/ui/flow-button';
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, Loader2, MailCheck, MailX, RotateCcw, ShieldCheck } from "lucide-react";
import {
  confirmDownloadAccessToken,
  getEmailPreferenceStatus,
  updateEmailPreference,
} from "lib/carrierFlowApi";
import { unavailableTmsOperation } from "lib/tmsConnection";

async function startPrivateDownload() {
  return unavailableTmsOperation();
}

function AccessShell({ eyebrow, title, copy, children }) {
  return (
    <main className="min-h-screen bg-canvas text-ink">
      <section className="relative isolate min-h-screen overflow-hidden border-b border-ink/10 py-28 sm:py-32">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 opacity-[0.035]"
          style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,0.72) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.72) 1px, transparent 1px)",
            backgroundSize: "58px 58px",
            maskImage: "radial-gradient(ellipse at center, black, transparent 78%)",
            WebkitMaskImage: "radial-gradient(ellipse at center, black, transparent 78%)",
          }}
        />
        <div className="absolute -top-40 right-[-12%] -z-10 h-[460px] w-[680px] rounded-full bg-[radial-gradient(circle,rgba(76,141,204,0.7),transparent_70%)] blur-[150px] opacity-[0.2]" />
        <span aria-hidden="true" className="pointer-events-none absolute bottom-0 right-[-2%] hidden select-none font-display text-[23vw] leading-none tracking-tighter text-ink/[0.035] lg:block">DL</span>
        <div className="absolute inset-0 grain-overlay" />
        <div className="site-content-shell relative z-10 grid grid-cols-1 gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}>
            <div className="mb-7 inline-flex items-center gap-3 rounded-full border border-gold/25 bg-gold/[0.06] px-4 py-2 font-body text-[10px] font-semibold uppercase tracking-[0.28em] text-accent-ink">
            {eyebrow}
            </div>
            <h1 className="font-display text-5xl font-semibold tracking-tight leading-[0.94] text-ink sm:text-6xl lg:text-7xl">
              {title}
            </h1>
            {copy ? <p className="mt-7 max-w-xl font-body text-lg leading-8 text-copy/58">{copy}</p> : null}
            <div className="mt-8 flex flex-wrap gap-2.5">
              {[
                ['Email', 'Verified first', MailCheck],
                ['Link', 'Private signed URL', ShieldCheck],
              ].map(([label, value, Icon]) => (
                <span key={label} className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 font-body text-[10px] uppercase tracking-[0.18em] text-copy/62">
                  <Icon className="h-3.5 w-3.5 text-accent-ink" strokeWidth={1.5} />
                  {label}: {value}
                </span>
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 34 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16, duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl border border-ink/10 bg-ink/[0.025] p-6 sm:p-8"
          >
            {children}
          </motion.div>
        </div>
      </section>
    </main>
  );
}

export function DownloadConfirmPage() {
  const { token = "" } = useParams();
  const [state, setState] = useState({ status: "loading", data: null, error: "", downloadStarted: false, downloadBusy: false });

  useEffect(() => {
    let mounted = true;
    setState({ status: "loading", data: null, error: "", downloadStarted: false, downloadBusy: false });
    confirmDownloadAccessToken(token).then((result) => {
      if (!mounted) return;
      if (!result.success) {
        setState({ status: "error", data: null, error: result.error || "This download confirmation link is invalid or expired.", downloadStarted: false, downloadBusy: false });
        return;
      }
      setState({ status: "ready", data: result.data, error: "", downloadStarted: false, downloadBusy: false });
      if (result.data?.downloadUrl) {
        window.setTimeout(() => {
          void startPrivateDownload(result.data.downloadUrl, token, result.data.strictFileToken).then((downloadStarted) => {
            if (!mounted) return;
            setState((current) => ({ ...current, downloadStarted }));
          });
        }, 900);
      }
    });
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleDownload = async () => {
    const downloadUrl = state.data?.downloadUrl || "";
    if (!downloadUrl) return;
    setState((current) => ({ ...current, downloadBusy: true, error: "" }));
    try {
      const downloadStarted = await startPrivateDownload(downloadUrl, token, state.data?.strictFileToken);
      setState((current) => ({ ...current, downloadStarted, downloadBusy: false }));
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error?.message || "The private download could not be started.",
        downloadBusy: false,
      }));
    }
  };

  const title = state.status === "error"
    ? "Download link unavailable"
    : state.data?.downloadOfferTitle || "Preparing your download";

  return (
    <AccessShell
      eyebrow="Download Access"
      title={title}
      copy="Magellan confirms access on this page, then opens the private file download."
    >
      {state.status === "loading" ? (
        <div className="flex items-center gap-3 font-body text-copy/64">
          <Loader2 className="h-5 w-5 animate-spin text-accent-ink" />
          Confirming your email and preparing the file link.
        </div>
      ) : null}

      {state.status === "ready" ? (
        <div>
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-accent-ink" />
            <div>
              <p className="font-body text-base leading-7 text-copy/70">
                {state.data?.message || "Your download is ready. The file should start opening automatically."}
              </p>
              {state.data?.downloadLinkExpiresAt ? (
                <p className="mt-3 font-body text-sm leading-6 text-copy/42">
                  This private link expires at {state.data.downloadLinkExpiresAt}.
                </p>
              ) : null}
              {state.downloadStarted ? (
                <p className="mt-3 font-body text-sm leading-6 text-accent-ink/75">
                  The download request was started from this website page.
                </p>
              ) : null}
              {state.error ? <p className="mt-3 font-body text-sm leading-6 text-red-300">{state.error}</p> : null}
            </div>
          </div>
          {state.data?.downloadUrl ? (
            <FlowButton
              type="button"
              onClick={handleDownload}
              disabled={state.downloadBusy}
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full  px-6 py-3 font-body text-xs font-bold uppercase tracking-[0.14em]  transition "
            >
              {state.downloadBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download ZIP
            </FlowButton>
          ) : (
            <FlowButton
              to="/#contact"
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full border border-ink/18 px-6 py-3 font-body text-xs font-bold uppercase tracking-[0.14em] text-ink transition hover:border-gold/60 "
            >
              Contact Support
            </FlowButton>
          )}
        </div>
      ) : null}

      {state.status === "error" ? (
        <div>
          <p className="font-body text-base leading-7 text-copy/68">{state.error}</p>
          <FlowButton
            to="/#contact"
            className="mt-7 inline-flex items-center justify-center gap-2 rounded-full border border-ink/18 px-6 py-3 font-body text-xs font-bold uppercase tracking-[0.14em] text-ink transition hover:border-gold/60 "
          >
            Contact Support
          </FlowButton>
        </div>
      ) : null}
    </AccessShell>
  );
}

export function EmailPreferencesPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const requestedAction = searchParams.get("action") === "unsubscribe" ? "unsubscribe" : "";
  const [state, setState] = useState({ status: "loading", data: null, error: "", actionBusy: "" });

  const title = useMemo(() => {
    if (requestedAction === "unsubscribe") return "Confirm unsubscribe";
    return "Email preferences";
  }, [requestedAction]);

  useEffect(() => {
    let mounted = true;
    setState({ status: "loading", data: null, error: "", actionBusy: "" });
    getEmailPreferenceStatus(token, requestedAction).then((result) => {
      if (!mounted) return;
      if (!result.success) {
        setState({ status: "error", data: null, error: result.error || "This email preference link is invalid or expired.", actionBusy: "" });
        return;
      }
      setState({ status: "ready", data: result.data, error: "", actionBusy: "" });
    });
    return () => {
      mounted = false;
    };
  }, [requestedAction, token]);

  const handleUpdate = async (action) => {
    setState((current) => ({ ...current, actionBusy: action, error: "" }));
    const result = await updateEmailPreference(token, action);
    if (!result.success) {
      setState((current) => ({ ...current, actionBusy: "", error: result.error || "Email preferences could not be updated." }));
      return;
    }
    setState({ status: "ready", data: result.data, error: "", actionBusy: "" });
  };

  return (
    <AccessShell
      eyebrow="Email Preferences"
      title={title}
      copy="Manage Magellan follow-up email preferences from the public website."
    >
      {state.status === "loading" ? (
        <div className="flex items-center gap-3 font-body text-copy/64">
          <Loader2 className="h-5 w-5 animate-spin text-accent-ink" />
          Loading email preferences.
        </div>
      ) : null}

      {state.status === "ready" ? (
        <div>
          <p className="font-body text-base leading-7 text-copy/70">
            {state.data?.message || "Email preference status loaded."}
          </p>
          <p className="mt-3 font-body text-sm leading-6 text-copy/42">
            {state.data?.email || ""}
          </p>
          {state.error ? <p className="mt-4 font-body text-sm leading-6 text-red-300">{state.error}</p> : null}
          <div className="mt-7 flex flex-wrap gap-3">
            <FlowButton
              type="button"
              onClick={() => handleUpdate("unsubscribe")}
              disabled={Boolean(state.actionBusy)}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/18 px-6 py-3 font-body text-xs font-bold uppercase tracking-[0.14em] text-ink transition hover:border-gold/60  disabled:opacity-60"
            >
              {state.actionBusy === "unsubscribe" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailX className="h-4 w-4" />}
              Unsubscribe
            </FlowButton>
            <FlowButton
              type="button"
              onClick={() => handleUpdate("subscribe")}
              disabled={Boolean(state.actionBusy)}
              className="inline-flex items-center justify-center gap-2 rounded-full  px-6 py-3 font-body text-xs font-bold uppercase tracking-[0.14em]  transition  disabled:opacity-60"
            >
              {state.actionBusy === "subscribe" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Keep Emails On
            </FlowButton>
          </div>
        </div>
      ) : null}

      {state.status === "error" ? (
        <div>
          <p className="font-body text-base leading-7 text-copy/68">{state.error}</p>
          <FlowButton
            to="/#contact"
            className="mt-7 inline-flex items-center justify-center gap-2 rounded-full border border-ink/18 px-6 py-3 font-body text-xs font-bold uppercase tracking-[0.14em] text-ink transition hover:border-gold/60 "
          >
            Contact Support
          </FlowButton>
        </div>
      ) : null}
    </AccessShell>
  );
}
